import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldAlert, LogOut, Mail, LayoutDashboard } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Routes that require an active (non-blocked) agent role.
// Everything else (dashboard, stores, account, payments, support, ...)
// remains accessible to the merchant side of the account.
const AGENT_ONLY_ROUTES = ['/agent-tasks', '/my-requests'];

/**
 * Global guard for verified agents. Every 5 minutes it calls
 * `/api/agent/suspension-status` (which proxies ASB606 admin).
 *
 * IMPORTANT: This ONLY blocks agent-scoped pages. If a user is also a
 * merchant (very common), they must still be able to access their stores,
 * dashboard, payments, etc. — only the agent workspace is locked.
 */
export default function AgentSuspensionGuard() {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [blocked, setBlocked] = useState(false);
  const [reason, setReason] = useState('');
  const timerRef = useRef(null);

  const checkStatus = async () => {
    const token = localStorage.getItem('business_token');
    if (!token) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/agent/suspension-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data?.blocked) {
        setReason(data.blocked_reason || '');
        setBlocked(true);
      } else {
        setBlocked(false);
      }
    } catch {
      // silent — fail-open
    }
  };

  useEffect(() => {
    checkStatus();
    timerRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);
    const onFocus = () => checkStatus();
    const onLogin = () => checkStatus();
    window.addEventListener('focus', onFocus);
    window.addEventListener('userLoggedIn', onLogin);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('userLoggedIn', onLogin);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-check on route change so a suspended agent visiting /agent-tasks
  // sees the modal without waiting up to 5 minutes.
  useEffect(() => {
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('business_token');
    localStorage.removeItem('business_user');
    window.location.href = '/login';
  };

  const goDashboard = () => {
    navigate('/dashboard');
  };

  // Only show the block modal on agent-scoped routes.
  // On merchant routes we render a lightweight top banner instead.
  const isOnAgentRoute = AGENT_ONLY_ROUTES.some(r => location.pathname.startsWith(r));

  if (!blocked) return null;

  // Merchant routes → subtle top banner (non-blocking)
  if (!isOnAgentRoute) {
    return (
      <div
        className="fixed top-16 sm:top-20 inset-x-0 z-40 flex justify-center px-4 pointer-events-none"
        data-testid="agent-suspension-banner"
      >
        <div className="pointer-events-auto max-w-2xl w-full rounded-xl border border-red-300 dark:border-red-500/40 bg-red-50/95 dark:bg-red-900/40 backdrop-blur-md shadow-lg px-4 py-2.5 flex items-center gap-3">
          <ShieldAlert className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-900 dark:text-red-100 flex-1">
            {language === 'ar'
              ? 'تم إيقاف عضوية الوكيل مؤقتاً. حسابك التجاري يعمل بشكل طبيعي.'
              : 'Your agent membership is suspended. Your merchant account still works normally.'}
          </p>
          <a
            href="mailto:support@cloudgm.business?subject=Agent%20Suspension%20Appeal"
            className="text-xs font-semibold text-red-700 dark:text-red-300 hover:underline whitespace-nowrap"
          >
            {language === 'ar' ? 'تواصل معنا' : 'Contact us'}
          </a>
        </div>
      </div>
    );
  }

  // Agent routes → full blocking modal
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="agent-suspension-modal"
    >
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-[#131B2C] border border-red-500/30 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-rose-600 p-5 text-white text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-3">
            <ShieldAlert className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold mb-1" data-testid="agent-suspension-title">
            {language === 'ar' ? 'تم إيقاف عضوية الوكيل' : 'Agent Membership Suspended'}
          </h2>
          <p className="text-sm text-white/85">
            {language === 'ar'
              ? 'لا يمكنك استخدام حساب الوكيل حالياً — لكن حسابك التجاري يعمل بشكل طبيعي'
              : "Your agent role is locked — your merchant account still works normally"}
          </p>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div className="rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-red-600 dark:text-red-400 mb-1">
              {language === 'ar' ? 'السبب' : 'Reason'}
            </p>
            <p className="text-sm text-red-900 dark:text-red-200" data-testid="agent-suspension-reason">
              {reason || (language === 'ar' ? 'لم يُقدَّم سبب من الإدارة.' : 'No reason provided by admin.')}
            </p>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {language === 'ar'
              ? 'إذا كنت تعتقد أن هذا خطأ، يمكنك التواصل مع فريق الدعم لمراجعة قرار الإيقاف. متجرك ومبيعاتك لن تتأثر.'
              : 'If you believe this is a mistake, contact support to review the suspension. Your stores and sales are unaffected.'}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
            <button
              onClick={goDashboard}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90"
              data-testid="agent-suspension-dashboard-btn"
            >
              <LayoutDashboard className="h-4 w-4" />
              {language === 'ar' ? 'الذهاب إلى متجري' : 'Go to my dashboard'}
            </button>
            <a
              href="mailto:support@cloudgm.business?subject=Agent%20Suspension%20Appeal"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 dark:border-white/10 text-gray-700 dark:text-white bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10"
              data-testid="agent-suspension-support-btn"
            >
              <Mail className="h-4 w-4" />
              {language === 'ar' ? 'تواصل مع الدعم' : 'Contact Support'}
            </a>
          </div>

          <button
            onClick={handleLogout}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            data-testid="agent-suspension-logout-btn"
          >
            <LogOut className="h-3.5 w-3.5" />
            {language === 'ar' ? 'تسجيل الخروج' : 'Logout'}
          </button>
        </div>
      </div>
    </div>
  );
}
